import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, X, Share, Plus, MoreVertical, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa-install-banner-dismissed-v2';

// Module-level singletons so multiple consumers share the same state.
let cachedPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<(p: BeforeInstallPromptEvent | null) => void>();

let guideOpen = false;
const guideListeners = new Set<(open: boolean) => void>();
const setGuideOpenGlobal = (open: boolean) => {
  guideOpen = open;
  guideListeners.forEach((l) => l(open));
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
    promptListeners.forEach((l) => l(cachedPrompt));
  });
  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    promptListeners.forEach((l) => l(null));
  });
}

function detectEnv() {
  if (typeof window === 'undefined') {
    return { isStandalone: false, isIOS: false, isInAppBrowser: false };
  }
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true;
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isInAppBrowser =
    /FBAN|FBAV|Instagram|Line|TikTok|Snapchat|Twitter|LinkedInApp|WhatsApp/i.test(ua);
  return { isStandalone, isIOS, isInAppBrowser };
}

/** Hook that exposes install state and a trigger usable from any component. */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(cachedPrompt);
  const [env, setEnv] = useState(detectEnv);
  const [showGuide, setShowGuide] = useState(guideOpen);

  useEffect(() => {
    setEnv(detectEnv());
    const promptListener = (p: BeforeInstallPromptEvent | null) => setDeferredPrompt(p);
    const guideListener = (open: boolean) => setShowGuide(open);
    promptListeners.add(promptListener);
    guideListeners.add(guideListener);
    return () => {
      promptListeners.delete(promptListener);
      guideListeners.delete(guideListener);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        cachedPrompt = null;
        setDeferredPrompt(null);
      }
      return;
    }
    setGuideOpenGlobal(true);
  }, [deferredPrompt]);

  return {
    ...env,
    canInstall: !env.isStandalone,
    hasNativePrompt: !!deferredPrompt,
    triggerInstall,
    showGuide,
    setShowGuide: setGuideOpenGlobal,
  };
}

/** Reusable install button — hides itself when the app is already installed. */
export const InstallButton = ({
  variant = 'outline',
  size = 'sm',
  className,
  label,
}: {
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  label?: string;
}) => {
  const { t } = useTranslation();
  const { canInstall, triggerInstall, showGuide, setShowGuide, isIOS, isInAppBrowser } = useInstallPrompt();
  if (!canInstall) return null;
  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={triggerInstall}
        className={cn('gap-1.5', className)}
      >
        <Download className="h-4 w-4" />
        <span>{label ?? t('pwa.installButton')}</span>
      </Button>
      <InstallGuideDialog
        open={showGuide}
        onOpenChange={setShowGuide}
        isIOS={isIOS}
        isInAppBrowser={isInAppBrowser}
      />
    </>
  );
};

const InstallGuideDialog = ({
  open,
  onOpenChange,
  isIOS,
  isInAppBrowser,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  isIOS: boolean;
  isInAppBrowser: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('pwa.installTitle')}</DialogTitle>
          <DialogDescription>
            {isIOS ? t('pwa.iosGuideIntro') : t('pwa.androidGuideIntro')}
          </DialogDescription>
        </DialogHeader>

        {isInAppBrowser && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t('pwa.inAppBrowserWarning')}</span>
          </div>
        )}

        {isIOS ? (
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</span>
              <span className="flex-1 flex items-center gap-2">
                {t('pwa.iosStep1')}
                <Share className="h-4 w-4 text-primary inline-block" />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">2</span>
              <span className="flex-1 flex items-center gap-2">
                {t('pwa.iosStep2')}
                <Plus className="h-4 w-4 text-primary inline-block" />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">3</span>
              <span className="flex-1">{t('pwa.iosStep3')}</span>
            </li>
          </ol>
        ) : (
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</span>
              <span className="flex-1 flex items-center gap-2">
                {t('pwa.androidStep1')}
                <MoreVertical className="h-4 w-4 text-primary inline-block" />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">2</span>
              <span className="flex-1">{t('pwa.androidStep2')}</span>
            </li>
          </ol>
        )}

        <Button onClick={() => onOpenChange(false)} className="w-full">
          {t('pwa.gotIt')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

/** Auto-shown subtle banner. Dismissed forever once closed. */
const InstallPrompt = () => {
  const { t } = useTranslation();
  const { canInstall, hasNativePrompt, isIOS, triggerInstall, showGuide, setShowGuide, isInAppBrowser } = useInstallPrompt();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!canInstall) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    // Only auto-show when we either have a native prompt or we're on iOS (where guide is the only path).
    if (!hasNativePrompt && !isIOS) return;
    const timer = setTimeout(() => setShowBanner(true), 3000);
    return () => clearTimeout(timer);
  }, [canInstall, hasNativePrompt, isIOS]);

  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  if (!canInstall) return null;

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-3 inset-x-3 sm:bottom-4 sm:inset-x-auto sm:end-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <div className="sm:max-w-sm rounded-2xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-2xl shadow-black/10 p-3">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                <Download className="h-4 w-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold leading-tight">{t('pwa.installTitle')}</p>
                <p className="text-[11px] text-muted-foreground leading-snug truncate">{t('pwa.installDesc')}</p>
              </div>
              <Button
                size="sm"
                className="h-8 px-3 text-xs shrink-0"
                onClick={async () => { await triggerInstall(); dismiss(); }}
              >
                {t('pwa.installButton')}
              </Button>
              <button
                onClick={dismiss}
                className="shrink-0 p-1 rounded-lg hover:bg-muted/50 text-muted-foreground"
                aria-label={t('pwa.later')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <InstallGuideDialog
        open={showGuide}
        onOpenChange={setShowGuide}
        isIOS={isIOS}
        isInAppBrowser={isInAppBrowser}
      />
    </>
  );
};

export default InstallPrompt;
